import React from 'react';
import {
    List,
    Spinner,
} from 'reactstrap';
import { uniqBy } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { Block, BlockBorder } from '../common/Block';
import {
    AppPageForm, toasty,
} from '../common/forms/FormElements';
import CreateHolidayForm from '../holiday/CreateHolidayForm';
import { SaveButton } from '../common/forms/SaveButton';
import { ExecutionHistory } from './common/ExecutionHistory';


class ManageGroupHoliday extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            loading: false,
            saving: false,
            isValidated: false,
            tenants: [],
            selectedTenant: null,
            groupOptions: [],
            selectedGroupOptions: [],
            availableHolidayOptions: [],
            selectedHolidays: [],
            originalHolidayIds: [],

            showAddHolidayForm: false,
        };
    }

    componentDidMount() {
        Promise.all([
            this.loadTenants(),
            this.loadAllGroups(),
        ]);
    }

    loadTenants = async () => {
        try {
            const tenants = await httpClient.js(ApiRoutes.typeAheads.tenants());
            this.setState({ tenants });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load tenants.');
        }
    };

    onTenantChanged = (selectedOption) => {
        this.setState(
            {
                selectedTenant: selectedOption,
                isValidated: false,
                selectedGroupOptions: [],
                selectedHolidays: [],
                originalHolidayIds: [],
                availableHolidayOptions: [],
            },
            () => {
                if (selectedOption?.value) {
                    this.loadAvailableHolidays(selectedOption.value);
                }
            });
    };

    loadAllGroups = async () => {
        try {
            const data = await httpClient.js(ApiRoutes.holiday.holidayGroupSearch());
            this.setState({ groupOptions: data });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load holiday groups.');
        }
    };

    loadAvailableHolidays = async (tenantId) => {
        try {
            const raw = await httpClient.js(ApiRoutes.holiday.all({ getAllHoliday: true, tenantId }));
            const availableHolidayOptions = raw.map((h) => ({
                label: h.name ?? h.label,
                value: h.id ?? h.value,
                date: (h.date ?? '').substring(0, 10),
            }));
            this.setState({ availableHolidayOptions });
        } catch (e) {
            console.error(e);
            toasty.error('Unable to load available holidays.');
        }
    };

    onGroupsChanged = async (selectedOption) => {
        const selectedOptions = selectedOption ? [selectedOption] : [];

        this.setState({
            loading: true,
            selectedGroupOptions: selectedOptions,
            selectedHolidays: [],
        });

        if (selectedOptions.length === 0) {
            return this.setState({ loading: false });
        }

        const idsPath = selectedOptions.map((o) => o.value).join(',');
        try {
            const tenantId = this.state.selectedTenant?.value;
            const groupHolidays = await httpClient.js(ApiRoutes.holiday.listGroupHolidays(idsPath, tenantId));

            const groupHolidayIds = groupHolidays.map((h) => h.id ?? h.value);

            this.setState((prev) => {
                const mergedOptions = uniqBy([
                    ...prev.availableHolidayOptions,
                    ...groupHolidays.map((h) => ({
                        label: h.name ?? h.label,
                        value: h.id ?? h.value,
                        date: (h.date ?? '').substring(0, 10),
                    })),
                ], 'value');

                return {
                    loading: false,
                    availableHolidayOptions: mergedOptions,
                    selectedHolidays: mergedOptions.filter((opt) => groupHolidayIds.includes(opt.value)),
                    originalHolidayIds: groupHolidayIds,
                    isValidated: false,
                };
            });
        } catch (err) {
            console.error(err);
            toasty.error('Unable to load holidays for selected group.');
            this.setState({ loading: false });
        }
    };


    onSubmit = async () => {
        const {
            selectedGroupOptions,
            availableHolidayOptions,
            selectedHolidays,
            originalHolidayIds = [],
        } = this.state;

        if (!this.state.selectedTenant) {
            this.setState({ isValidated: true });
            toasty.error('Please choose a tenant.');
            return;
        }

        if (selectedGroupOptions.length === 0) {
            this.setState({ isValidated: true });
            toasty.error('Please choose a holiday group.');
            return;
        }

        const newSelectedIds = selectedHolidays.map((h) => h.value);

        const addedDates = availableHolidayOptions
            .filter((h) =>
                newSelectedIds.includes(h.value) &&
                !originalHolidayIds.includes(h.value),
            )
            .map(({ value: Id, date, label: Name }) =>
                date
                    ? { Id, Date: date, Name }
                    : { Id },
            );

        const removedDateIds = originalHolidayIds.filter((id) => !newSelectedIds.includes(id));

        this.setState({ saving: true });

        try {
            await Promise.all(
                selectedGroupOptions.map((opt) => httpClient.post(
                    ApiRoutes.adminTool.upsertHolidayGroup(),
                    {
                        tenantId: this.state.selectedTenant?.value,
                        tenantHolidayGroupId: opt.value,
                        addedDates,
                        removedDateIds,
                    },
                )),
            );
            toasty.success('Holiday group saved');
            await this.onGroupsChanged(selectedGroupOptions[0]);
        } catch (err) {
            console.error(err);
            toasty.error('Could not save holidays – see console for details');
        } finally {
            this.setState({ saving: false });
            await this.props.refreshUsageHistory();
        }
    };

    render() {
        const {
            loading,
            saving,
            isValidated,
            selectedGroupOptions,
            groupOptions,
            tenants,
            selectedTenant,
            availableHolidayOptions,
            selectedHolidays,
            showAddHolidayForm,
        } = this.state;

        if (!this.context?.user) return null;

        return (
            <>
                <AppPageForm
                    backUrl={AppNavPaths.AdminTool}
                    backLabel="Back to Admin Tools"
                    formShown={this.context.formIsOpen}
                    formId="manageGroupHolidayForm"
                    formName="manageGroupHolidayForm"
                    formHeading="Manage Group Holidays"
                    formRef={this.formRef}
                    formIsColumn
                    saving={saving}
                    isValidated={isValidated}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => this.setState({ isValidated: value })}
                >
                    <Block padding="medium">
                        <div>
                            <ValidatedSelect
                                id="selectedTenant"
                                label="Select a tenant"
                                name="selectedTenant"
                                required
                                options={tenants}
                                value={selectedTenant}
                                onChange={this.onTenantChanged}
                                placeholder="Select a tenant"
                                error="Select a tenant"
                                invalid={isValidated && !selectedTenant}
                            />

                            <ValidatedSelect
                                id="holidayGroup"
                                name="holidayGroup"
                                label="Holiday Group"
                                formGroupClassName="mt-3"
                                options={groupOptions}
                                value={selectedGroupOptions[0] || null}
                                onChange={this.onGroupsChanged}
                                placeholder="Select a holiday group"
                                isDisabled={!selectedTenant}
                                invalid={isValidated && selectedGroupOptions.length === 0}
                            />

                            {loading && (
                                <div className="d-flex justify-content-center my-2">
                                    <Spinner size="sm" />
                                    <span className="ml-2 text-muted">Loading holidays … </span>
                                </div>
                            )}

                            {selectedGroupOptions.length > 0 && (
                                <ValidatedSelect
                                    id="groupHolidaySelect"
                                    name="observedHolidays"
                                    label="Observed Holidays"
                                    isMulti
                                    options={availableHolidayOptions}
                                    value={selectedHolidays}
                                    onChange={(newValue) => this.setState({ selectedHolidays: newValue || [] })}
                                    helperText={selectedHolidays.length === 0 &&
                                        'No holidays selected – this group will be treated as observing none.'
                                    }
                                    showAddButton
                                    onAddButtonClick={() => this.setState({ showAddHolidayForm: true })}
                                />
                            )}

                            <BlockBorder />
                            <div className="d-flex justify-content-center">
                                <SaveButton
                                    saving={saving}
                                    label="Submit"
                                />
                            </div>
                        </div>
                    </Block>

                    <ExecutionHistory usageHistory={this.props.usageHistory}>
                        {(record) => {
                            if (!record?.output) return null;

                            const { output } = record;

                            const groupName = output.groupName ?? output.holidayGroup;

                            const added = output.Added ?? output.added ?? [];
                            const updated = output.Updated ?? output.updated ?? [];
                            const removed = output.Removed ?? output.removed ?? [];

                            return (
                                <List type="unstyled">
                                    {groupName && (
                                        <li>
                                            <strong>Group:</strong>
                                            {' '}
                                            {groupName}
                                        </li>
                                    )}

                                    {added.length > 0 && (
                                        <li>
                                            <strong>Added Holidays:</strong>
                                            <ul>
                                                {added.map((h, i) => <li key={i}>{`${h.date} - ${h.name}`}</li>)}
                                            </ul>
                                        </li>
                                    )}

                                    {updated.length > 0 && (
                                        <li>
                                            <strong>Updated Holidays:</strong>
                                            <ul>
                                                {updated.map((h, i) => <li key={i}>{`${h.date} - ${h.name}`}</li>)}
                                            </ul>
                                        </li>
                                    )}

                                    {removed.length > 0 && (
                                        <li>
                                            <strong>Removed Holidays:</strong>
                                            <ul>
                                                {removed.map((h, i) => <li key={i}>{`${h.date} - ${h.name}`}</li>)}
                                            </ul>
                                        </li>
                                    )}
                                </List>
                            );
                        }}
                    </ExecutionHistory>
                </AppPageForm>

                <CreateHolidayForm
                    show={showAddHolidayForm}
                    isCompanyHoliday={false}
                    holidayGroupTenantId={selectedTenant?.value || null}
                    isGroupedHoliday
                    onHolidayCreated={(newHolidayOption) => {
                        this.setState((prev) => ({
                            availableHolidayOptions: [...prev.availableHolidayOptions, newHolidayOption],
                            selectedHolidays: [...prev.selectedHolidays, newHolidayOption],
                            showAddHolidayForm: false,
                        }));
                    }}
                    onClose={() => this.setState({ showAddHolidayForm: false })}
                />
            </>
        );
    }
}

export default ManageGroupHoliday;
